import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UnitPicker } from "@/components/UnitPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GST_RATES = [0, 5, 12, 18, 28];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    hsn_sac: "",
    price: "",
    gst_rate: "18",
    unit: "PCS",
    stock_group: "",
    opening_stock: "0",
    reorder_level: "0",
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const filtered = products.filter(
      (product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.hsn_sac.includes(searchQuery)
    );
    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        gst_rate: parseFloat(formData.gst_rate),
        opening_stock: parseFloat(formData.opening_stock) || 0,
        reorder_level: parseFloat(formData.reorder_level) || 0,
      };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
      } else {
        await axios.post(`${API}/products`, data);
      }
      fetchProducts();
      resetForm();
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      hsn_sac: "",
      price: "",
      gst_rate: "18",
      unit: "PCS",
      stock_group: "",
      opening_stock: "0",
      reorder_level: "0",
    });
    setEditingProduct(null);
    setIsDialogOpen(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      hsn_sac: product.hsn_sac,
      price: product.price.toString(),
      gst_rate: product.gst_rate.toString(),
      unit: product.unit,
      stock_group: product.stock_group || "",
      opening_stock: (product.opening_stock ?? 0).toString(),
      reorder_level: (product.reorder_level ?? 0).toString(),
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="p-4 sm:p-8" data-testid="products-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">
            Products & Services
          </h1>
          <p className="text-slate-500 text-sm">Manage your product catalog</p>
        </div>
        <button
          onClick={() => setIsDialogOpen(true)}
          data-testid="add-product-btn"
          className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2"
        >
          <Plus size={20} weight="bold" />
          Add Product
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <MagnifyingGlass
          size={20}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="text"
          data-testid="search-products-input"
          placeholder="Search by name or HSN/SAC code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
      </div>

      {/* Products Table */}
      <div className="bg-white border border-slate-200 rounded-sm">
        {loading ? (
          <div className="p-12 animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-slate-200 rounded"></div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-12 text-center">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/c2875c4c-3209-4e71-8b15-de610df9cec1/images/6f94e3573c58a84c73fc78db7a1660d6d91c6b0e4489f8721400bcc17382d354.png"
              alt="No products"
              className="w-64 h-64 mx-auto mb-6 opacity-60"
            />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No products found</h3>
            <p className="text-slate-500 mb-6">Add your first product to get started</p>
            <button
              onClick={() => setIsDialogOpen(true)}
              data-testid="empty-add-product-btn"
              className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold hover:bg-blue-700 transition-all duration-200"
            >
              Add Product
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto sticky-table">
            <table className="w-full">
              <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Product Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    HSN/SAC
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Price
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    GST Rate
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Unit
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Stock
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredProducts.map((product) => (
                  <tr
                    key={product.id}
                    data-testid={`product-row-${product.id}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {product.name}
                        </p>
                        {product.description && (
                          <p className="text-xs text-slate-500 mt-1">
                            {product.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-600">
                      {product.hsn_sac}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                      ₹{product.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {product.gst_rate}%
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {product.unit}
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const stock = product.current_stock ?? 0;
                        const reorder = product.reorder_level ?? 0;
                        let cls = "bg-emerald-100 text-emerald-700"; // healthy
                        let lbl = `${stock} ${product.unit}`;
                        if (stock <= 0) {
                          cls = "bg-red-100 text-red-700";
                          lbl = stock < 0 ? `${stock} ${product.unit}` : `Out of stock`;
                        } else if (stock <= reorder && reorder > 0) {
                          cls = "bg-amber-100 text-amber-800";
                        }
                        return (
                          <span
                            data-testid={`product-stock-${product.id}`}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}
                          >
                            {lbl}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-sm transition-all duration-200"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                          className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="max-w-2xl"
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            const t = e.detail?.originalEvent?.target || e.target;
            if (t?.closest?.('[data-group-picker-panel]')) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Product/Service Name *</Label>
              <Input
                id="name"
                data-testid="product-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                autoCap
                data-testid="product-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="hsn_sac">HSN/SAC Code *</Label>
                <Input
                  id="hsn_sac"
                  data-testid="product-hsn-input"
                  value={formData.hsn_sac}
                  onChange={(e) => setFormData({ ...formData, hsn_sac: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="price">Price *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  data-testid="product-price-input"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="unit">Unit *</Label>
                <UnitPicker
                  value={formData.unit}
                  onChange={(abbr) => setFormData({ ...formData, unit: abbr })}
                  testid="product-unit-picker"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="stock_group">Stock Group</Label>
              <Input
                id="stock_group"
                data-testid="product-stock-group-input"
                value={formData.stock_group}
                onChange={(e) => setFormData({ ...formData, stock_group: e.target.value })}
                placeholder="e.g. Plastic Waste (blank = Primary)"
              />
              <p className="text-[10px] text-slate-400 mt-1">Used in Inventory Books → Group Summary. Leave blank to keep under Primary.</p>
            </div>

            <div>
              <Label htmlFor="gst_rate">GST Rate *</Label>
              <select
                id="gst_rate"
                data-testid="product-gst-select"
                value={formData.gst_rate}
                onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                {GST_RATES.map((rate) => (
                  <option key={rate} value={rate}>
                    {rate}%
                  </option>
                ))}
              </select>
            </div>

            {/* INVENTORY: opening stock seeds the running balance; reorder
                level drives the colour pill on the products list. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <div>
                <Label htmlFor="opening_stock">Opening Stock</Label>
                <Input
                  id="opening_stock"
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="product-opening-stock-input"
                  value={formData.opening_stock}
                  onChange={(e) => setFormData({ ...formData, opening_stock: e.target.value })}
                  placeholder="0"
                />
                <p className="text-[10px] text-slate-400 mt-1">Current on-hand quantity. Set once; live stock updates on sale &amp; purchase.</p>
              </div>
              <div>
                <Label htmlFor="reorder_level">Reorder Level</Label>
                <Input
                  id="reorder_level"
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="product-reorder-level-input"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                  placeholder="0"
                />
                <p className="text-[10px] text-slate-400 mt-1">Stock at or below this number turns the badge amber as a low-stock warning.</p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-product-btn">
                {editingProduct ? "Update" : "Add"} Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
